import jobServices from 'ApiService/Admin/jobServices';
import studentService from 'ApiService/Admin/studentService';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import Nodatafound from 'pages/Common/Nodatafound';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import StudentHistory from './StudentHistory';

const Index = ({
  classData,
  attendance,
  getStudents,
  currentStep,
  disable,
  handleNext,
  handleBack,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [studentDetailsLoading, setStudentDetailsLoading] = useState(false);
  const [studentPauseStatuses, setStudentPauseStatuses] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    if (classData) {
      setLoading(true);
      getStudents().finally(() => setLoading(false));
    }
  }, [classData, getStudents]);

  useEffect(() => {
    setFilteredAttendance(
      attendance.filter((student) =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, attendance]);

  useEffect(() => {
    const fetchAllStudentsPauseStatus = async () => {
      setLoadingStatus(true);
      try {
        if (attendance.length === 0) {
          return;
        }

        const studentIds = attendance.map((student) => student.id);
        const response = await jobServices.getAllStudentsPauseStatus(
          studentIds
        );

        if (response.data.success) {
          const statusMap = response.data.pauseStatuses.reduce(
            (acc, status) => {
              acc[status.studentId] = status;
              return acc;
            },
            {}
          );
          setStudentPauseStatuses(statusMap);
        }
      } catch (error) {
        toast.error('Failed to fetch student pause statuses');
      } finally {
        setLoadingStatus(false);
      }
    };

    if (attendance.length > 0) {
      fetchAllStudentsPauseStatus();
    } else {
      setLoadingStatus(false);
    }
  }, [attendance]);

  const fetchStudentDetails = async (student) => {
    setStudentDetailsLoading(true);
    try {
      const response = await studentService.getStudentPauseInfo(student.id);

      if (response.data.success) {
        setSelectedStudent(response.data.data);
        setShowDetails(true);
      } else {
        toast.error('Failed to fetch student details');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Error fetching student details'
      );
    } finally {
      setStudentDetailsLoading(false);
    }
  };

  const handleStudentClick = (student) => {
    fetchStudentDetails(student);
  };

  const handleBackToList = () => {
    setShowDetails(false);
    setSelectedStudent(null);
  };

  if (showDetails) {
    return (
      <Card extra="w-full sm:overflow-auto p-4">
        {studentDetailsLoading ? (
          <div className="flex h-64 items-center justify-center">
            <BallTriangle color="#5431ed" />
          </div>
        ) : (
          <StudentHistory studentData={selectedStudent} />
        )}
        <div className="flex flex-col justify-end px-3 pt-2 md:flex-row md:px-8">
          <button
            onClick={handleBackToList}
            className="mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0"
          >
            Back to List
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card extra="w-full sm:overflow-auto p-4">
      <div className="mb-4">
        <InputField
          label="Search by Name"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Enter student name..."
        />
      </div>
      <div>
        {loading || loadingStatus ? (
          <div className="flex h-64 items-center justify-center">
            <BallTriangle color="#5431ed" />
          </div>
        ) : filteredAttendance.length > 0 ? (
          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            <table className="w-full">
              <thead className="w-full">
                <tr className="border-b border-gray-200 dark:border-white/10">
                  <th className="px-[27px] pb-[12px] pt-[32px] text-center text-xs tracking-wide text-gray-600">
                    <div className="text-xs font-bold">SR.NO</div>
                  </th>
                  <th className="px-[27px] pb-[12px] pt-[32px] text-center text-xs tracking-wide text-gray-600">
                    <div className="text-xs font-bold">NAME</div>
                  </th>
                  <th className="px-[27px] pb-[12px] pt-[32px] text-center text-xs tracking-wide text-gray-600">
                    <div className="text-xs font-bold">STATUS</div>
                  </th>
                  <th className="px-[27px] pb-[12px] pt-[32px] text-center text-xs tracking-wide text-gray-600">
                    <div className="text-xs font-bold">
                      HAS PAUSE / UNPAUSE HISTORY
                    </div>
                  </th>
                  <th className="px-[27px] pb-[12px] pt-[32px] text-center text-xs tracking-wide text-gray-600">
                    <div className="text-xs font-bold">ACTIONS</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAttendance.map((student, index) => {
                  const pauseStatus = studentPauseStatuses[student.id] || {};
                  const isPlacementPausedOverall =
                    pauseStatus.isPlacementPaused ||
                    pauseStatus.isPlacementPausedByAdmin;
                  const isPermanentPause = pauseStatus.pauseInfo?.isPermanent;
                  const studentStatus = pauseStatus.status || 'none';

                  return (
                    <tr
                      key={student.id}
                      className="border-b border-gray-200 dark:border-white/10"
                    >
                      <td className="px-[27px] py-[16px] text-center text-navy-700 dark:text-white">
                        {index + 1}
                      </td>
                      <td className="px-[27px] py-[16px] text-center text-navy-700 dark:text-white">
                        {student.name}
                      </td>
                      <td className="px-[27px] py-[16px] text-center text-navy-700 dark:text-white">
                        {isPermanentPause ? (
                          <span className="font-medium text-red-500">
                            Permanently Paused
                          </span>
                        ) : isPlacementPausedOverall ? (
                          <span className="font-medium text-yellow-500">
                            Temporarily Paused
                          </span>
                        ) : (
                          <span className="font-medium text-green-500">
                            Active
                          </span>
                        )}
                        {isPlacementPausedOverall &&
                          studentStatus !== 'none' && (
                            <div className="mt-1">
                              {studentStatus === 'pending' ? (
                                <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs text-yellow-800">
                                  Pending Approval
                                </span>
                              ) : studentStatus === 'approved' ? (
                                <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                                  Approved
                                </span>
                              ) : studentStatus === 'rejected' ? (
                                <span className="rounded-full bg-red-100 px-2 py-1 text-xs text-red-800">
                                  Rejected
                                </span>
                              ) : null}
                            </div>
                          )}
                      </td>
                      <td className="px-[27px] py-[16px] text-center text-navy-700 dark:text-white">
                        {pauseStatus?.pauseInfo ? 'Yes' : 'No'}
                      </td>
                      <td className="px-[27px] py-[16px] text-center text-navy-700 dark:text-white">
                        <button
                          onClick={() => handleStudentClick(student)}
                          className="rounded-xl bg-brand-500 px-4 py-1 text-xs font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Nodatafound title="No matching attendance data found." />
        )}
      </div>
    </Card>
  );
};

export default Index;
